import {
  type InstalledPlugin,
  isUserPluginSource,
  pluginContributes,
} from '@crowdex/plugin-contracts';
import { Chip } from '@crowdex/ui/components/chip';
import { cn } from '@crowdex/ui/lib/utils';
import { ArrowUp, LoaderCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { settingButtonClass } from '@/components/settings-page/setting-controls';
import { useRouter } from '@/lib/navigation';
import { pluginsPath } from '@/lib/plugins/plugin-sections';
import type { usePluginManagement } from '@/lib/plugins-page/use-plugin-management';
import { isMachineSkillGroup, MachineSkillsSections } from './machine-skills';
import { CONTRIBUTION_LABELS, InstalledPluginRow } from './plugin-card';

/** The kinds the catalog and Installed filter on, in the boards' order. */
export const KIND_FILTERS = ['connectors', 'agents', 'apps', 'skills', 'mcp'] as const;

/** A filter of the list, with its count. */
export function FilterPill({
  selected,
  onClick,
  children,
  count,
}: {
  selected: boolean;
  onClick: () => void;
  children: ReactNode;
  count?: number;
}) {
  return (
    <Chip onClick={onClick} selected={selected} size="md">
      {children}
      {count !== undefined && <span className="text-muted-foreground">{count}</span>}
    </Chip>
  );
}

export function PluginsLoading() {
  return (
    <div className="flex h-56 items-center justify-center gap-2 text-muted-foreground text-sm">
      <LoaderCircle className="size-4 animate-spin" /> Loading plugins…
    </div>
  );
}

/**
 * Plugins › Installed (D31, phone 28): kinds with their counts, each plugin with its switch and
 * its waiting update; on a phone, the user's own plugins below.
 */
export function InstalledPlugins({
  management,
  phone,
  accountCount,
  onOpen,
}: {
  management: ReturnType<typeof usePluginManagement>;
  phone: boolean;
  accountCount: (plugin: InstalledPlugin) => number;
  onOpen: (plugin: InstalledPlugin) => void;
}) {
  const router = useRouter();
  const {
    activeKind,
    busyPlugin,
    installed,
    listed,
    loading,
    setActiveKind,
    shownPlugins,
    toggle,
  } = management;
  const pending = installed.filter((plugin) => plugin.crowdex?.pending !== undefined);
  const updateAll = () => {
    void (async () => {
      for (const plugin of pending) await management.approveUpdate(plugin);
    })();
  };
  const kinds = KIND_FILTERS.map((kind) => ({
    kind,
    count: listed.filter((plugin) => pluginContributes(plugin, kind)).length,
  })).filter((entry) => entry.count > 0);
  // This computer's skills are listed apart, by the folder they are in.
  const custom = listed.filter(
    (plugin) => isUserPluginSource(plugin.source) && !isMachineSkillGroup(plugin),
  );
  const machine = (shownPlugins as InstalledPlugin[]).filter(isMachineSkillGroup);
  const rows = (shownPlugins as InstalledPlugin[]).filter(
    // A phone lists the user's own below.
    (plugin) => !isMachineSkillGroup(plugin) && (!phone || !isUserPluginSource(plugin.source)),
  );
  return (
    <>
      {phone && pending.length > 0 && (
        <div className="mb-4 flex items-center gap-3 rounded-2xl bg-running/15 px-4 py-3">
          <ArrowUp aria-hidden className="size-4 text-running" />
          <span className="min-w-0 flex-1 text-base">
            {pending.length} {pending.length === 1 ? 'update' : 'updates'} available
          </span>
          <button className={settingButtonClass('primary')} onClick={updateAll} type="button">
            Update all
          </button>
        </div>
      )}
      {!phone && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <FilterPill
            count={listed.length}
            onClick={() => setActiveKind(null)}
            selected={activeKind === null}
          >
            All
          </FilterPill>
          {kinds.map(({ kind, count }) => (
            <FilterPill
              count={count}
              key={kind}
              onClick={() => setActiveKind(kind)}
              selected={activeKind === kind}
            >
              {CONTRIBUTION_LABELS[kind]}
            </FilterPill>
          ))}
          {pending.length > 0 && (
            <button
              className={cn(settingButtonClass('primary'), 'ml-auto')}
              onClick={updateAll}
              type="button"
            >
              Update all
            </button>
          )}
        </div>
      )}
      {loading ? (
        <PluginsLoading />
      ) : rows.length === 0 && machine.length === 0 ? (
        <p className="rounded-2xl border border-hairline border-dashed py-16 text-center text-muted-foreground text-sm">
          No plugins installed yet.
        </p>
      ) : rows.length === 0 ? null : (
        <div className="overflow-hidden rounded-2xl border bg-card">
          {rows.map((plugin) => (
            <InstalledPluginRow
              accounts={accountCount(plugin)}
              busy={busyPlugin === plugin.name}
              key={plugin.name}
              onOpen={onOpen}
              onToggle={toggle}
              onUpdate={
                plugin.crowdex?.pending ? () => void management.approveUpdate(plugin) : undefined
              }
              phone={phone}
              plugin={plugin}
            />
          ))}
        </div>
      )}
      {!loading && machine.length > 0 && (
        <MachineSkillsSections
          busyPlugin={busyPlugin}
          groups={machine}
          onOpen={onOpen}
          onRemove={management.removeMachineSkills}
          onToggle={toggle}
          phone={phone}
          removable={management.desktop}
        />
      )}
      {phone && (
        <section className="mt-6">
          <p className="mb-2 px-1 text-muted-foreground text-sm">
            Custom · your own plugins, MCP servers and skills
          </p>
          <div className="overflow-hidden rounded-2xl border bg-card">
            {custom.map((plugin) => (
              <InstalledPluginRow
                accounts={accountCount(plugin)}
                busy={busyPlugin === plugin.name}
                key={plugin.name}
                onOpen={onOpen}
                onToggle={toggle}
                phone
                plugin={plugin}
              />
            ))}
            <button
              className="flex w-full items-center gap-3 px-4 py-3 text-left text-base"
              onClick={() => router.push(pluginsPath('custom'))}
              type="button"
            >
              <span aria-hidden className="text-base">
                +
              </span>
              Add an MCP server, a skill or a marketplace
            </button>
          </div>
          <p className="mt-3 px-1 text-muted-foreground text-xs">
            Importing a local folder is only on a computer.
          </p>
        </section>
      )}
    </>
  );
}
